import { Link } from 'react-router-dom';
import { ArrowLeft, Users, Heart, GraduationCap, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { valori } from '../data/siteData';

const iconMap = {
  1: Users,
  2: Heart,
  3: GraduationCap
};

export const Valori = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="valori-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#002060] to-[#003380] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna alla home
          </Link>
          
          <div className="text-center">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5CB9E5] mb-4 block">
              Chi Siamo
            </span>
            <h1 className="font-serif text-4xl md:text-6xl font-bold text-white mb-6">
              I Nostri Valori
            </h1>
            <p className="text-white/80 max-w-3xl mx-auto text-lg">
              I principi fondamentali che guidano la nostra azione politica
            </p>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-12 bg-[#FFD700]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <blockquote className="font-serif text-lg md:text-xl text-[#002060] italic leading-relaxed">
            "Siamo convinti che solo attraverso la collaborazione, la partecipazione e il dialogo possiamo costruire un futuro migliore per tutti. Continuiamo a lavorare con impegno, dedizione e determinazione, ascoltando le vostre esigenze e le vostre necessità, cercando soluzioni concrete e innovative per migliorare la qualità della vita delle persone."
          </blockquote>
        </div>
      </section>

      {/* Valori Cards */}
      <section className="py-16 bg-white" data-testid="valori-cards-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            {valori.map((valore) => {
              const IconComponent = iconMap[valore.id];
              return (
                <div
                  key={valore.id}
                  className="bg-[#F4F6F8] rounded-xl p-8 border-t-4 border-[#5CB9E5] card-hover"
                  data-testid={`valore-card-${valore.id}`}
                >
                  <div className="p-4 bg-[#002060] rounded-lg w-fit mb-6">
                    <IconComponent className="w-8 h-8 text-[#FFD700]" />
                  </div>
                  <span className="text-xs uppercase tracking-widest font-semibold text-[#5CB9E5] mb-2 block">
                    Valore {valore.id}
                  </span>
                  <h3 className="font-serif text-2xl font-bold text-[#002060] mb-4">
                    {valore.titolo}
                  </h3>
                  <p className="text-slate-600 leading-relaxed line-clamp-4">
                    {valore.contenuto.split('\n\n')[0]}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed Accordion */}
      <section className="py-16 bg-[#F4F6F8]" data-testid="valori-accordion-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#002060] text-center mb-12">
            Approfondisci i nostri valori
          </h2>

          <Accordion type="single" collapsible className="space-y-4">
            {valori.map((valore) => {
              const IconComponent = iconMap[valore.id];
              return (
                <AccordionItem 
                  key={valore.id} 
                  value={`item-${valore.id}`}
                  className="bg-white rounded-xl shadow-sm border-none overflow-hidden"
                >
                  <AccordionTrigger className="px-6 py-5 hover:no-underline hover:bg-[#002060]/5 [&[data-state=open]]:bg-[#002060] [&[data-state=open]]:text-white transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-[#5CB9E5]/20 rounded-lg">
                        <IconComponent className="w-5 h-5 text-[#002060] [&[data-state=open]]:text-white" />
                      </div>
                      <span className="font-serif text-lg font-semibold text-left">
                        {valore.titolo}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 py-6 text-slate-700 leading-relaxed">
                    {valore.contenuto.split('\n\n').map((paragraph, idx) => (
                      <p key={idx} className="mb-4 last:mb-0">
                        {paragraph}
                      </p>
                    ))}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#002060]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl font-bold text-white mb-4">
            Condividi i nostri valori?
          </h2>
          <p className="text-white/80 mb-8">
            Unisciti a noi per costruire insieme il futuro della Valle d'Aosta
          </p>
          <Link to="/unisciti">
            <Button className="btn-gold px-8 py-3 rounded-lg font-semibold flex items-center gap-2 mx-auto">
              Entra nella squadra
              <ChevronRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
